import { useQuery } from '@tanstack/react-query';
import { AssetClassName, Entity, mapEntity } from '@toggle/toggle';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  formatPriceDisplay,
  getPriceStamp,
} from '~/components/price-display-view/helpers/price-display-helpers';
import { mapEntityToPriceEntity, useLivePrice } from '~/hooks/use-live-price';
import { useSnakeMeta } from '~/hooks/use-snake/useSnake';
import { getPreliminaryPrice } from '~/services/price/price-service';
import { globalPriceFormatter } from '~/utils/asset-price/assetPrice';
import { getPriceData } from '~/widgets/asset-overview-widget/utils/asset-box/asset-box-helpers';

import * as S from './EarningsLivePrice.styles';

interface EntityLivePriceProps {
  entity: Entity;
}

export const EarningsLivePrice = ({ entity }: EntityLivePriceProps) => {
  const { t } = useTranslation(['analyze', 'countries', 'asset-overview']);

  const mappedEntity = mapEntity(entity);

  const { data: snakeMeta } = useSnakeMeta(entity?.default_snake);

  const { data: preliminaryPrice } = useQuery({
    queryKey: ['preliminaryPrice', entity.subscribable_ticker],
    queryFn: () => getPreliminaryPrice(entity.subscribable_ticker as string),
    enabled: !!entity.subscribable_ticker,
  });

  const priceEntities = mapEntityToPriceEntity(entity, snakeMeta);
  const livePriceData = useLivePrice(priceEntities);
  const livePriceInfo = getPriceData({
    livePriceData,
    entity: mappedEntity,
    ts: [],
    priceEntity: priceEntities[0],
  });

  const priceFormatter = useCallback(
    (price: number) =>
      globalPriceFormatter({
        entity: mappedEntity,
        price,
        priceSuffix: '',
        shouldShowCurrency: false,
      }),
    [mappedEntity]
  );

  if (
    (!livePriceInfo && snakeMeta?.last_value === undefined) ||
    !snakeMeta?.last_timestamp
  ) {
    return null;
  }

  const price = formatPriceDisplay({
    exchange: mappedEntity.exchange.code,
    livePriceInfo,
    preliminaryPrice: preliminaryPrice?.data,
    meta: snakeMeta,
    isHovered: false,
    t,
  });

  const isLivePriceSupported = !!(
    mappedEntity?.subscribable_ticker &&
    mappedEntity.asset_class === AssetClassName.Equity
  );

  const primaryStamp = getPriceStamp({
    isLivePriceSupported,
    isLive: price.primary?.isLive || false,
    isDisabled: false,
  });

  const secondaryStamp = price.secondary
    ? getPriceStamp({
        isLivePriceSupported,
        isLive: price.secondary?.isLive || false,
        isDisabled: false,
      })
    : null;

  return (
    <S.Container data-testid="earnings-live-price">
      {price.primary.price !== undefined && (
        <>
          <S.PriceWrapper $isPrimary>
            <S.Price>{priceFormatter(price.primary.price)}</S.Price>
            <S.Currency>{mappedEntity.currency}</S.Currency>
            {primaryStamp.stampType === 'notSupported' ? (
              <S.SquareStamp $color={primaryStamp.color} />
            ) : (
              <S.CircleStamp $color={primaryStamp.color} />
            )}
          </S.PriceWrapper>
          {price.primary.priceChange && (
            <S.PriceChangeContainer $status={price.primary.priceChange.status}>
              <span>{price.primary.priceChange.change}</span>
              <span>{price.primary.priceChange.proportionChange}</span>
            </S.PriceChangeContainer>
          )}
        </>
      )}
      {price.secondary !== undefined &&
        !!price.secondary.price &&
        !!price.secondary.isLive && (
          <>
            <S.PriceWrapper $isPrimary={false}>
              <S.Price>{priceFormatter(price.secondary.price)}</S.Price>
              <S.Currency>{mappedEntity.currency}</S.Currency>
              {secondaryStamp &&
                (secondaryStamp.stampType === 'notSupported' ? (
                  <S.SquareStamp $color={secondaryStamp.color} />
                ) : (
                  <S.CircleStamp $color={secondaryStamp.color} />
                ))}
            </S.PriceWrapper>
            {price.secondary.priceChange && (
              <S.PriceChangeContainer
                $status={price.secondary.priceChange.status}
              >
                <span>{price.secondary.priceChange.change}</span>
                <span>{price.secondary.priceChange.proportionChange}</span>
              </S.PriceChangeContainer>
            )}
          </>
        )}
    </S.Container>
  );
};
